import * as forms from "@client/forms";
import * as widgets from "@client/forms/widgets";
import * as hardware from "@client/hardware";
import {Radio, RadioGroup} from "@headlessui/react";
import {CheckIcon} from "@heroicons/react/20/solid";
import {classNames, server} from "@reactivated";
import {Decimal} from "decimal.js";
import {motion} from "motion/react";
import React from "react";

type Gender = "MALE" | "FEMALE";

type ProfileValues = {
    name: string;
    date_of_birth: string | null;
    birth_month: string | null;
    birth_day: string | null;
    birth_year: string | null;
    gender: Gender | null;
    feet: "2" | "3" | "4" | "5" | "6" | "7" | "8" | null;
    inches:
        | "0"
        | "1"
        | "2"
        | "3"
        | "4"
        | "5"
        | "6"
        | "7"
        | "8"
        | "9"
        | "10"
        | "11"
        | null;
    heightCm: string | null;
    weightLb: string | null;
    weightKg: string | null;
};

type Units = "IMPERIAL" | "METRIC";

function convertGenderFromProfile(gender: string | null | undefined): Gender | null {
    if (gender === "m") return "MALE";
    if (gender === "f") return "FEMALE";
    return null;
}

// One useId per option instance: the card's title names the option and its
// description describes it, rather than both running together into one name.
function BigRadioOption<TEnum extends string>(props: {
    value: TEnum;
    label: React.ReactNode;
    description: React.ReactNode;
    extra: React.ReactNode;
}) {
    const generated = React.useId();
    const labelId = `${generated}-label`;
    const descriptionId = `${generated}-description`;

    return (
        <Radio
            as={forms.AssociatedOption}
            value={props.value}
            labelledBy={labelId}
            describedBy={props.description != null ? descriptionId : undefined}
            className={({checked}: {checked: boolean}) =>
                classNames(
                    "block w-full cursor-pointer rounded-xl border p-4 text-left transition-all",
                    checked
                        ? "bg-white border-indigo-600"
                        : "border-stone-200 bg-white",
                )
            }
        >
            {({checked}: {checked: boolean}) => (
                <div className="flex items-center justify-between">
                    <div className="flex-1">
                        <div
                            className={classNames(
                                "flex items-center gap-2",
                                props.description != null && "mb-0.5",
                            )}
                        >
                            <span
                                id={labelId}
                                className="text-base font-normal text-gray-900"
                            >
                                {props.label}
                            </span>
                            {props.extra}
                        </div>
                        {props.description != null && (
                            <p id={descriptionId} className="text-sm text-gray-600">
                                {props.description}
                            </p>
                        )}
                    </div>
                    <div
                        aria-hidden="true"
                        style={
                            checked
                                ? {
                                      borderColor: "#4F46E5",
                                      backgroundColor: "#4F46E5",
                                  }
                                : {}
                        }
                        className={classNames(
                            "flex h-6 w-6 items-center justify-center rounded-full border-2 shrink-0 ml-3",
                            checked ? "" : "border-stone-300",
                        )}
                    >
                        {checked && (
                            <CheckIcon
                                className="size-3.5 text-white"
                                strokeWidth={3}
                            />
                        )}
                    </div>
                </div>
            )}
        </Radio>
    );
}

export function BigRadioSelect<TEnum extends string>(
    props: forms.EnumWidgetProps<
        TEnum,
        {
            descriptions: Record<TEnum, React.ReactNode | false>;
            extras?: Partial<Record<TEnum, React.ReactNode>>;
            horizontal?: boolean;
        }
    >,
) {
    const filtered = props.field.options
        .map(([setting, label]) => {
            if (setting == null) return null;
            const description = props.descriptions[setting];

            if (description === false) {
                return null;
            }
            return {
                setting,
                label,
                description,
            };
        })
        .filter((o) => o != null);

    const labelledBy = props["aria-labelledby"];

    return (
        <RadioGroup
            as={forms.AssociatedGroup}
            id={props.id}
            labelledBy={labelledBy}
            describedBy={props["aria-describedby"]}
            aria-label={
                labelledBy == null
                    ? (props["aria-label"] ?? props.field.label)
                    : undefined
            }
            aria-invalid={props["aria-invalid"]}
            value={props.field.value}
            onChange={(setting: TEnum) => {
                void hardware.haptic();
                props.field.onChange(setting);
            }}
            className={classNames(
                "flex gap-3",
                props.horizontal === true ? "flex-row" : "flex-col",
                props.className,
            )}
        >
            {filtered.map(({setting, label, description}) => (
                <BigRadioOption
                    key={setting}
                    value={setting}
                    label={label}
                    description={description}
                    extra={props.extras?.[setting]}
                />
            ))}
        </RadioGroup>
    );
}

const generateWeightOptions = (unitSystem: Units): number[] => {
    if (unitSystem === "IMPERIAL") {
        return Array.from({length: 93}, (_, i) => i * 5 + 40);
    } else {
        return Array.from({length: 47}, (_, i) => i * 5 + 20);
    }
};

const generateYearOptions = (): [string, string][] => {
    const currentYear = new Date().getFullYear();
    const years: [string, string][] = [[null as unknown as string, "Year"]];
    for (let year = currentYear; year >= currentYear - 100; year--) {
        years.push([year.toString(), year.toString()]);
    }
    return years;
};

const MONTH_OPTIONS: [string | null, string][] = [
    [null, "Month"],
    ["01", "January"],
    ["02", "February"],
    ["03", "March"],
    ["04", "April"],
    ["05", "May"],
    ["06", "June"],
    ["07", "July"],
    ["08", "August"],
    ["09", "September"],
    ["10", "October"],
    ["11", "November"],
    ["12", "December"],
];

const generateDayOptions = (): [string | null, string][] => {
    const days: [string | null, string][] = [[null, "Day"]];
    for (let day = 1; day <= 31; day++) {
        const dayStr = day.toString().padStart(2, "0");
        days.push([dayStr, day.toString()]);
    }
    return days;
};

function parseDateOfBirth(dateOfBirth: string | null): {
    month: string | null;
    day: string | null;
    year: string | null;
} {
    if (dateOfBirth == null) {
        return {month: null, day: null, year: null};
    }
    const parts = dateOfBirth.split("-");
    if (parts.length !== 3) {
        return {month: null, day: null, year: null};
    }
    return {
        year: parts[0],
        month: parts[1],
        day: parts[2],
    };
}

function composeDateOfBirth(
    year: string | null,
    month: string | null,
    day: string | null,
): string | null {
    if (year == null || month == null || day == null) {
        return null;
    }
    return `${year}-${month}-${day}`;
}

function calculateDefaultHeightImperial(heightCm: string | null): {
    defaultFeet: ProfileValues["feet"];
    defaultInches: ProfileValues["inches"];
} {
    if (heightCm == null) {
        return {defaultFeet: null, defaultInches: null};
    }

    const heightInInches = new Decimal(heightCm).div(2.54);
    const feet = heightInInches.div(12).floor().toString() as ProfileValues["feet"];
    const inches = heightInInches.mod(12).floor().toString() as ProfileValues["inches"];

    return {defaultFeet: feet, defaultInches: inches};
}

function calculateHeightCmFromImperial(
    feet: ProfileValues["feet"],
    inches: ProfileValues["inches"],
): string | null {
    if (feet == null || inches == null) {
        return null;
    }

    const totalInches = new Decimal(feet).mul(12).plus(inches);
    const cm = totalInches.mul(2.54);
    return cm.toString();
}

// The wizard's inputs hand over raw text, so a cleared field is "" and a
// half-typed one may not parse; neither converts.
const parseMeasurement = (value: string | null) => {
    if (value == null || value.trim() === "") {
        return null;
    }
    try {
        const parsed = new Decimal(value.trim());
        return parsed.isNaN() ? null : parsed;
    } catch {
        return null;
    }
};

const nearestWeightOption = (weight: Decimal, units: Units) =>
    generateWeightOptions(units)
        .reduce((prev, curr) =>
            Math.abs(curr - weight.toNumber()) < Math.abs(prev - weight.toNumber())
                ? curr
                : prev,
        )
        .toString();

// Switching units carries the other system's values over, snapped to the
// weight selects' 5-unit steps and whole inches.
export const convertUnits = (
    values: ProfileValues & {units: Units},
    units: Units,
): ProfileValues & {units: Units} => {
    if (units === "METRIC") {
        const weightLb = parseMeasurement(values.weightLb);
        return {
            ...values,
            units,
            heightCm:
                calculateHeightCmFromImperial(values.feet, values.inches) ??
                values.heightCm,
            weightKg:
                weightLb == null
                    ? values.weightKg
                    : nearestWeightOption(weightLb.div(2.20462), "METRIC"),
        };
    }
    const heightCm = parseMeasurement(values.heightCm);
    const weightKg = parseMeasurement(values.weightKg);
    const {defaultFeet, defaultInches} = calculateDefaultHeightImperial(
        heightCm?.toString() ?? null,
    );
    return {
        ...values,
        units,
        feet: defaultFeet ?? values.feet,
        inches: defaultInches ?? values.inches,
        weightLb:
            weightKg == null
                ? values.weightLb
                : nearestWeightOption(weightKg.mul(2.20462), "IMPERIAL"),
    };
};

function calculateDefaultWeights(lastWeightKg: string | null): {
    defaultWeightLb: string | null;
    defaultWeightKg: string | null;
} {
    if (lastWeightKg == null) {
        return {defaultWeightLb: null, defaultWeightKg: null};
    }

    const lastWeightInKgDecimal = new Decimal(lastWeightKg);
    let calculatedWeightLb: string | null = null;
    let calculatedWeightKg: string | null = null;

    // Calculate for Imperial
    const lastWeightInLbs = lastWeightInKgDecimal.mul(2.20462);
    const lbOptionsValues = generateWeightOptions("IMPERIAL");
    if (lbOptionsValues.length > 0) {
        calculatedWeightLb = lbOptionsValues
            .reduce((prev, curr) =>
                Math.abs(curr - lastWeightInLbs.toNumber()) <
                Math.abs(prev - lastWeightInLbs.toNumber())
                    ? curr
                    : prev,
            )
            .toString();
    }

    // Calculate for Metric
    const kgOptionsValues = generateWeightOptions("METRIC");
    if (kgOptionsValues.length > 0) {
        calculatedWeightKg = kgOptionsValues
            .reduce((prev, curr) =>
                Math.abs(curr - lastWeightInKgDecimal.toNumber()) <
                Math.abs(prev - lastWeightInKgDecimal.toNumber())
                    ? curr
                    : prev,
            )
            .toString();
    }

    return {
        defaultWeightLb: calculatedWeightLb,
        defaultWeightKg: calculatedWeightKg,
    };
}

const itemVariants = {
    hidden: {opacity: 0, y: 20, scale: 0.97},
    show: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {duration: 0.5, ease: [0.16, 1, 0.3, 1] as const},
    },
};

export function useProfile(props?: {
    profile?: server.core.rpc.Profile.output | null;
    lastWeight?: string | null;
    withName?: boolean;
    withWeight?: boolean;
    initial?: Partial<ProfileValues & {units: Units}>;
}) {
    const ids = React.useId();
    const {defaultFeet, defaultInches} = calculateDefaultHeightImperial(
        props?.profile?.height ?? null,
    );
    const {defaultWeightLb, defaultWeightKg} = calculateDefaultWeights(
        props?.lastWeight ?? null,
    );

    const initialUnits: Units =
        props?.profile?.is_imperial === false ? "METRIC" : "IMPERIAL";

    const initialDateParts = parseDateOfBirth(props?.profile?.date_of_birth ?? null);

    const [values, setValues] = React.useState<ProfileValues & {units: Units}>({
        name: props?.profile?.name ?? "",
        date_of_birth: props?.profile?.date_of_birth ?? null,
        birth_month: initialDateParts.month,
        birth_day: initialDateParts.day,
        birth_year: initialDateParts.year,
        gender: convertGenderFromProfile(props?.profile?.gender),
        feet: defaultFeet,
        inches: defaultInches,
        heightCm: props?.profile?.height ?? null,
        weightLb: defaultWeightLb,
        weightKg: defaultWeightKg,
        units: initialUnits,
        ...props?.initial,
    });

    const form = forms.useForm(
        {
            fields: {
                name: {
                    kind: "text",
                },
                date_of_birth: {
                    kind: "date",
                    nullable: true,
                },
                birth_month: {
                    kind: "select",
                    nullable: true,
                    required: false,
                    widget: "select",
                    options: MONTH_OPTIONS,
                },
                birth_day: {
                    kind: "select",
                    nullable: true,
                    required: false,
                    widget: "select",
                    options: generateDayOptions(),
                },
                birth_year: {
                    kind: "select",
                    nullable: true,
                    required: false,
                    widget: "select",
                    options: generateYearOptions(),
                },
                gender: {
                    kind: "select",
                    nullable: true,
                    widget: "quick",
                    options: [
                        ["MALE", "Male"],
                        ["FEMALE", "Female"],
                    ],
                },
                feet: {
                    kind: "select",
                    nullable: true,
                    required: false,
                    widget: "select",
                    options: [
                        [null, "Feet"],
                        ["2", "2 ft"],
                        ["3", "3 ft"],
                        ["4", "4 ft"],
                        ["5", "5 ft"],
                        ["6", "6 ft"],
                        ["7", "7 ft"],
                        ["8", "8 ft"],
                    ],
                },
                inches: {
                    kind: "select",
                    nullable: true,
                    required: false,
                    widget: "select",
                    options: [
                        [null, "Inches"],
                        ["0", "0 in"],
                        ["1", "1 in"],
                        ["2", "2 in"],
                        ["3", "3 in"],
                        ["4", "4 in"],
                        ["5", "5 in"],
                        ["6", "6 in"],
                        ["7", "7 in"],
                        ["8", "8 in"],
                        ["9", "9 in"],
                        ["10", "10 in"],
                        ["11", "11 in"],
                    ],
                },
                heightCm: {
                    kind: "decimal",
                    nullable: true,
                },
                weightLb: {
                    kind: "select",
                    nullable: true,
                    required: false,
                    widget: "select",
                    options: (
                        [[null, "Select weight"]] as [string | null, string][]
                    ).concat(
                        generateWeightOptions("IMPERIAL").map((value) => [
                            value.toString(),
                            `${value} lbs`,
                        ]),
                    ),
                },
                weightKg: {
                    kind: "select",
                    nullable: true,
                    required: false,
                    widget: "select",
                    options: (
                        [[null, "Select weight"]] as [string | null, string][]
                    ).concat(
                        generateWeightOptions("METRIC").map((value) => [
                            value.toString(),
                            `${value} kg`,
                        ]),
                    ),
                },
                units: {
                    kind: "select",
                    nullable: false,
                    required: true,
                    widget: "select",
                    options: [
                        ["IMPERIAL", "feet and inches"],
                        ["METRIC", "centimeters"],
                    ],
                },
            },
        },
        {
            value: values,
            onChange: (next) =>
                setValues((prev) =>
                    next.units === prev.units ? next : convertUnits(prev, next.units),
                ),
        },
    );

    // Compute date_of_birth from month, day, year
    React.useEffect(() => {
        const composedDate = composeDateOfBirth(
            form.values.birth_year,
            form.values.birth_month,
            form.values.birth_day,
        );
        if (composedDate !== form.values.date_of_birth) {
            setValues((prev) => ({
                ...prev,
                date_of_birth: composedDate,
            }));
        }
    }, [form.values.birth_month, form.values.birth_day, form.values.birth_year]);

    const withName = props?.withName ?? false;
    const withWeight = props?.withWeight ?? true;

    const isComplete =
        form.values.date_of_birth != null &&
        form.values.gender != null &&
        (withName === false || form.values.name.trim() !== "") &&
        ((form.values.units === "IMPERIAL" &&
            form.values.feet != null &&
            form.values.inches != null &&
            (withWeight === false || form.values.weightLb != null)) ||
            (form.values.units === "METRIC" &&
                form.values.heightCm != null &&
                (withWeight === false || form.values.weightKg != null)));

    const component = (
        <>
            {withName && (
                <motion.label
                    className="flex flex-col gap-2 text-lg font-light"
                    variants={itemVariants}
                >
                    <div>What should we call you?</div>
                    <div className="flex w-full items-center rounded-sm border border-stone-300 bg-white pr-1 has-focus:ring-2 has-focus:ring-indigo-500">
                        <input
                            type="text"
                            name="name"
                            className="flex-1 border-0 bg-transparent px-6 py-4 placeholder:font-inter placeholder:font-light placeholder:text-stone-400 focus:ring-0 focus:outline-hidden"
                            value={form.values.name}
                            onChange={(e) => {
                                setValues((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                }));
                            }}
                        />
                    </div>
                </motion.label>
            )}

            <motion.label
                className="flex flex-col gap-2 text-lg font-light"
                variants={itemVariants}
            >
                <div>When were you born?</div>
                <div className="flex justify-between gap-2 [text-align-last:center]">
                    <div className="flex w-full items-center rounded-sm border border-stone-300 bg-white pr-1 has-focus:ring-2 has-focus:ring-indigo-500">
                        <forms.Widget
                            field={form.fields.birth_month}
                            className="form-select form-select-tiny-arrow flex-1 border-0 bg-transparent p-4 placeholder:font-inter placeholder:font-thin placeholder:text-stone-400 focus:ring-0 focus:outline-hidden"
                        />
                    </div>
                    <div className="flex w-full items-center rounded-sm border border-stone-300 bg-white pr-1 has-focus:ring-2 has-focus:ring-indigo-500">
                        <forms.Widget
                            field={form.fields.birth_day}
                            className="form-select form-select-tiny-arrow flex-1 border-0 bg-transparent p-4 placeholder:font-inter placeholder:font-thin placeholder:text-stone-400 focus:ring-0 focus:outline-hidden"
                        />
                    </div>
                    <div className="flex w-full items-center rounded-sm border border-stone-300 bg-white pr-1 has-focus:ring-2 has-focus:ring-indigo-500">
                        <forms.Widget
                            field={form.fields.birth_year}
                            className="form-select form-select-tiny-arrow flex-1 border-0 bg-transparent p-4 placeholder:font-inter placeholder:font-thin placeholder:text-stone-400 focus:ring-0 focus:outline-hidden"
                        />
                    </div>
                </div>
            </motion.label>

            <motion.label
                className="flex flex-col gap-2 text-lg font-light"
                variants={itemVariants}
            >
                <div>
                    How tall are you in{" "}
                    <forms.Widget
                        field={form.fields.units}
                        className={classNames(
                            "form-select text-lg px-0 py-0 bg-none bg-transparent border-0 text-indigo-600 font-light focus:ring-indigo-500 focus:ring-1 rounded-sm",
                            form.values.units === "IMPERIAL" ? "w-32" : "w-24",
                        )}
                    />
                    ?
                </div>
                {form.values.units === "IMPERIAL" ? (
                    <div className="flex gap-4 [text-align-last:center]">
                        <div className="flex w-full items-center rounded-sm border border-stone-300 bg-white pr-1 has-focus:ring-2 has-focus:ring-indigo-500">
                            <forms.Widget
                                field={form.fields.feet}
                                className="form-select flex-1 border-0 bg-transparent px-6 py-4 placeholder:font-inter placeholder:font-thin placeholder:text-stone-400 focus:ring-0 focus:outline-hidden"
                            />
                        </div>
                        <div className="flex w-full items-center rounded-sm border border-stone-300 bg-white pr-1 has-focus:ring-2 has-focus:ring-indigo-500">
                            <forms.Widget
                                field={form.fields.inches}
                                className="form-select flex-1 border-0 bg-transparent px-6 py-4 placeholder:font-inter placeholder:font-thin placeholder:text-stone-400 focus:ring-0 focus:outline-hidden"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="flex w-full items-center rounded-sm border border-stone-300 bg-white pr-1 has-focus:ring-2 has-focus:ring-indigo-500">
                        <widgets.LengthInput
                            className="flex-1 border-0 bg-transparent px-6 py-4 placeholder:font-inter placeholder:font-thin placeholder:text-stone-400 focus:ring-0 focus:outline-hidden"
                            isImperial={false}
                            name="heightCm"
                            ariaLabel="height"
                            value={form.values.heightCm}
                            onChange={(value) => {
                                setValues((prev) => ({
                                    ...prev,
                                    heightCm: value,
                                }));
                            }}
                            readOnly={false}
                        />
                        <div className="relative -left-4 text-stone-500">cm</div>
                    </div>
                )}
            </motion.label>

            {withWeight && (
                <motion.label
                    className="flex flex-col gap-2 text-lg font-light"
                    variants={itemVariants}
                >
                    <div>What's your approximate weight?</div>
                    {form.values.units === "IMPERIAL" ? (
                        <div className="flex gap-4 [text-align-last:center]">
                            <div className="flex w-full items-center rounded-sm border border-stone-300 bg-white pr-1 has-focus:ring-2 has-focus:ring-indigo-500">
                                <forms.Widget
                                    field={form.fields.weightLb}
                                    className="form-select flex-1 border-0 bg-transparent px-6 py-4 placeholder:font-inter placeholder:font-thin placeholder:text-stone-400 focus:ring-0 focus:outline-hidden"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="flex gap-4 [text-align-last:center]">
                            <div className="flex w-full items-center rounded-sm border border-stone-300 bg-white pr-1 has-focus:ring-2 has-focus:ring-indigo-500">
                                <forms.Widget
                                    field={form.fields.weightKg}
                                    className="form-select flex-1 border-0 bg-transparent px-6 py-4 placeholder:font-inter placeholder:font-thin placeholder:text-stone-400 focus:ring-0 focus:outline-hidden"
                                />
                            </div>
                        </div>
                    )}
                </motion.label>
            )}

            <motion.div
                className="flex flex-col gap-2 text-lg font-light"
                variants={itemVariants}
            >
                <div id={`${ids}-gender`}>What's your gender?</div>
                <BigRadioSelect
                    horizontal
                    aria-labelledby={`${ids}-gender`}
                    field={form.fields.gender}
                    descriptions={{
                        MALE: undefined,
                        FEMALE: undefined,
                    }}
                />
            </motion.div>
        </>
    );

    const normalizedHeight =
        form.values.units === "IMPERIAL"
            ? calculateHeightCmFromImperial(form.values.feet, form.values.inches)
            : form.values.heightCm;

    return {
        form,
        values: form.values,
        normalizedHeight,
        isComplete,
        component,
    };
}
